import * as React from "react";
import { css } from "@emotion/core";
import { Dialog, DialogBody, InputGroup } from "@blueprintjs/core";
import { IKnowledgeBaseArticle } from "../../../api/knowledgeBase";
import { flattenArticlesToDocumentOrder } from "../utils";
import useSearchKeyboard from "./useSearchKeyboard";
import SearchResults from "./SearchResults";
import SearchSkeleton from "./SearchSkeleton";

type SearchDialogProps = {
  isOpen: boolean;
  searchQuery: string;
  onSearchQueryChange: (value: string) => void;
  searchResults: IKnowledgeBaseArticle[];
  allArticles: IKnowledgeBaseArticle[] | undefined;
  isSearching: boolean;
  hasFetchedSearch: boolean;
  onClose: () => void;
  onSelectResult: (articleId: number) => void;
  getBreadcrumbTitles: (articleId: number) => string[];
};

const SearchDialog = ({
  isOpen,
  searchQuery,
  onSearchQueryChange,
  searchResults,
  allArticles,
  isSearching,
  hasFetchedSearch,
  onClose,
  onSelectResult,
  getBreadcrumbTitles,
}: SearchDialogProps) => {
  const searchInputRef = React.useRef<HTMLInputElement | null>(null);
  const hasSearchQuery = searchQuery.trim().length > 0;

  const allArticlesOrdered = React.useMemo(
    () => flattenArticlesToDocumentOrder(allArticles || []),
    [allArticles]
  );

  const displayResults = React.useMemo(() => {
    if (!hasSearchQuery) return allArticlesOrdered;
    if (!hasFetchedSearch) return allArticlesOrdered;
    return searchResults;
  }, [
    allArticlesOrdered,
    hasFetchedSearch,
    hasSearchQuery,
    isSearching,
    searchResults,
  ]);

  const handleSelect = React.useCallback(
    (articleId: number) => {
      onSelectResult(articleId);
    },
    [onSelectResult]
  );

  const { activeIndex, setActiveIndex, handleKeyDown } = useSearchKeyboard({
    isOpen,
    results: displayResults,
    hasSearchQuery,
    onSelectResult: handleSelect,
    onClose,
    onClearSearch: () => onSearchQueryChange(""),
  });

  React.useEffect(() => {
    if (!isOpen) return;
    const timer = window.requestAnimationFrame(() => {
      if (searchInputRef.current) {
        searchInputRef.current.focus();
        searchInputRef.current.select();
      }
    });
    return () => window.cancelAnimationFrame(timer);
  }, [isOpen]);

  return (
    <Dialog
      isOpen={isOpen}
      onClose={onClose}
      icon="search"
      className="bp6-dark"
      css={css`
        width: 768px;
        border-radius: 8px;
      `}
    >
      <DialogBody
        css={css`
          padding: 0 0 1rem 0;
        `}
      >
        <div
          css={css`
            display: flex;
            flex-direction: column;
          `}
        >
          <div
            css={css`
              padding: 1rem;
            `}
          >
            <InputGroup
              autoFocus
              leftIcon="search"
              placeholder="Search articles..."
              value={searchQuery}
              onChange={(e) => onSearchQueryChange(e.target.value)}
              inputRef={searchInputRef}
              onKeyDown={handleKeyDown}
            />
          </div>

          <div
            css={css`
              border-top: 1px solid var(--neutral-6);
              border-bottom: 1px solid var(--neutral-6);
              display: flex;
              flex-direction: column;
              gap: 0.5rem;
              height: 50vh;
              overflow-y: auto;
              padding: 1rem;
            `}
          >
            {isSearching && <SearchSkeleton />}

            {!isSearching &&
              hasFetchedSearch &&
              hasSearchQuery &&
              searchResults.length === 0 && (
                <div
                  css={css`
                    flex: 1;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    padding: 1rem;
                  `}
                >
                  <div
                    css={css`
                      background: var(--neutral-6-translucent);
                      border: 1px solid var(--neutral-6);
                      border-radius: 8px;
                      padding: 1.25rem 1.5rem;
                      width: 100%;
                      max-width: 400px;
                      text-align: center;
                    `}
                  >
                    <div
                      css={css`
                        font-size: 1rem;
                        font-weight: 700;
                        color: var(--neutral-2);
                        margin-bottom: 0.25rem;
                      `}
                    >
                      No results
                    </div>
                    <div
                      css={css`
                        color: var(--neutral-5);
                        font-size: 0.95rem;
                        margin-bottom: 0.75rem;
                      `}
                    >
                      Try a different keyword or adjust your search.
                    </div>
                    <button
                      type="button"
                      onClick={() => onSearchQueryChange("")}
                      css={css`
                        border: 0;
                        background: transparent;
                        color: var(--primary);
                        font-weight: 600;
                        cursor: pointer;
                        padding: 0.35rem 0.5rem;
                        border-radius: 4px;
                        transition: background 120ms ease;

                        &:hover {
                          background: rgba(255, 255, 255, 0.04);
                        }
                      `}
                    >
                      Clear search
                    </button>
                  </div>
                </div>
              )}

            {!isSearching && displayResults.length > 0 && (
              <SearchResults
                searchResults={displayResults}
                searchQuery={searchQuery}
                activeIndex={activeIndex}
                onSelectResult={handleSelect}
                onHoverResult={(idx) => setActiveIndex(idx)}
                getBreadcrumbTitles={getBreadcrumbTitles}
              />
            )}
          </div>
        </div>
      </DialogBody>
    </Dialog>
  );
};

export default SearchDialog;
